import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/pfc";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PricingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });
  const stopped = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null, attempts: 0 }); return; }
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, attempts });
          return;
        }
        attempts += 1;
        setState({ status: "polling", data, attempts });
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        setState({ status: "error", data: null, attempts });
      }
    };
    poll();
    return () => { stopped.current = true; };
  }, [sessionId]);

  return (
    <div className="max-w-[800px] mx-auto px-5 lg:px-10 py-24 text-center" data-testid="pricing-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="mx-auto text-pfc-gold animate-spin mb-6" size={48} />
          <div className="label text-pfc-gold mb-3">Processing</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white uppercase">Confirming your <span className="gold-text">payment</span>…</h1>
          <p className="text-white/60 mt-4 font-body">Hang tight, this usually takes just a moment.</p>
        </>
      )}
      {state.status === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="mx-auto text-pfc-gold mb-6" size={64} />
          <div className="label text-pfc-gold mb-3">Payment Confirmed</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-[0.95]">
            Welcome to the <span className="gold-text">Campaign.</span>
          </h1>
          <p className="text-white/70 mt-6 font-body text-lg">
            You’re now confirmed for <strong className="text-pfc-gold">{state.data?.metadata?.package_name || "your package"}</strong>.<br />
            Our editorial team will reach out within 1 business day.
          </p>
          <div className="flex gap-3 justify-center mt-10 flex-wrap">
            <Link to="/dashboard" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition" data-testid="success-dashboard">
              Go to Dashboard
            </Link>
            <Link to="/" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 border border-white/20 text-white hover:border-pfc-gold transition">
              Back to Home
            </Link>
          </div>
        </div>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <div data-testid="payment-failed">
          <XCircle className="mx-auto text-pfc-red mb-6" size={64} />
          <div className="label text-pfc-red mb-3">{state.status === "expired" ? "Session Expired" : "Confirmation Pending"}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white uppercase">Something <span className="gold-text">went sideways.</span></h1>
          <p className="text-white/70 mt-4 font-body">
            {state.status === "timeout"
              ? "Payment confirmation is still pending. Check your email — Stripe usually emails a receipt within minutes."
              : "Your checkout session couldn't be confirmed. No payment was processed."}
          </p>
          <div className="flex gap-3 justify-center mt-10 flex-wrap">
            <Link to="/pricing" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition">
              Try Again
            </Link>
            <Link to="/contact" className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 border border-white/20 text-white hover:border-pfc-gold transition">
              Contact Support
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
